export const AboboCrypto = {
  uInt8ArrayToBase64: (uint8array) => {
    let binary = '';
    const len = uint8array.byteLength;
    for (var i = 0; i < len; i++) {
      binary += String.fromCharCode(uint8array[i]);
    }
    return window.btoa(binary);
  },
  base64ToUint8Array: (base64) => {
    var binaryString = window.atob(base64);
    var bytes = new Uint8Array(binaryString.length);
    for (var i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  },
  removePemHeaders: (pem) => {
    const pemPublicHeader = '-----BEGIN PUBLIC KEY-----';
    const pemPublicFooter = '-----END PUBLIC KEY-----';
    const pemPrivateHeader = '-----BEGIN PRIVATE KEY-----';
    const pemPrivateFooter = '-----END PRIVATE KEY-----';
    let pemContents = pem;

    if (pemContents.includes(pemPublicHeader)) {
      pemContents = pemContents.substring(pemPublicHeader.length);
      if (pemContents.includes(pemPublicFooter)) {
        pemContents = pemContents.substring(
          0,
          pemContents.length - pemPublicFooter.length - 1
        );
      }
      return pemContents;
    }

    if (pemContents.includes(pemPrivateHeader)) {
      pemContents = pemContents.substring(pemPrivateHeader.length);
      if (pemContents.includes(pemPrivateFooter)) {
        pemContents = pemContents.substring(
          0,
          pemContents.length - pemPrivateFooter.length - 1
        );
      }
      return pemContents;
    }

    return pemContents;
  },
  importPrivateKey: (binaryDer) => {
    return window.crypto.subtle.importKey(
      'pkcs8',
      binaryDer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      false,
      ['decrypt', 'unwrapKey']
    );
  },
  importPrivateSignatureKey: (binaryDer) => {
    return window.crypto.subtle.importKey(
      'pkcs8',
      binaryDer,
      {
        name: 'RSA-PSS',
        hash: 'SHA-256',
      },
      false,
      ['sign']
    );
  },
  importPublicKey: (pem) => {
    const barePem = AboboCrypto.removePemHeaders(pem);
    // base64 decode the string to get the binary data and convert it to an ArrayBuffer
    const binaryDer = AboboCrypto.base64ToUint8Array(barePem);

    return window.crypto.subtle.importKey(
      'spki',
      binaryDer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      false,
      ['encrypt', 'wrapKey']
    );
  },
  importPublicSignatureKey: (pem) => {
    const barePem = AboboCrypto.removePemHeaders(pem);
    // base64 decode the string to get the binary data and convert it to an ArrayBuffer
    const binaryDer = AboboCrypto.base64ToUint8Array(barePem);

    return window.crypto.subtle.importKey(
      'spki',
      binaryDer,
      {
        name: 'RSA-PSS',
        hash: 'SHA-256',
      },
      false,
      ['verify']
    );
  },
  importAesGcmKey: (rawKeyData, exportable = false) => {
    return window.crypto.subtle.importKey(
      'raw',
      rawKeyData,
      { name: 'AES-GCM' },
      exportable,
      ['decrypt']
    );
  },
  generateAesGcmKey: () => {
    return window.crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256,
      },
      true,
      ['encrypt']
    );
  },

  generateInitializationVector: () => {
    return window.crypto.getRandomValues(new Uint8Array(12));
  },
  generateDigest: (message) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    return window.crypto.subtle.digest('SHA-256', data).then((buffer) => {
      return new Uint8Array(buffer);
    });
  },
  digest: (buffer) => {
    return window.crypto.subtle.digest('SHA-256', buffer).then((buffer) => {
      return new Uint8Array(buffer);
    });
  },
  sign: (data, key) => {
    return window.crypto.subtle
      .sign(
        {
          name: 'RSA-PSS',
          saltLength: 32,
        },
        key,
        data
      )
      .then((buffer) => new Uint8Array(buffer));
  },
  combineUint8Arrays: (...arrays) => {
    const totalLength = arrays.reduce((acc, array) => acc + array.length, 0);
    const combinedArray = new Uint8Array(totalLength);
    let offset = 0;
    arrays.forEach((array) => {
      combinedArray.set(array, offset);
      offset += array.length;
    });
    return combinedArray;
  },
  deriveKey: async (password, salt) => {
    const rawSalt = AboboCrypto.base64ToUint8Array(salt);
    const enc = new TextEncoder();
    const deriver = await window.crypto.subtle.importKey(
      'raw',
      enc.encode(password),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    return window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: rawSalt,
        iterations: 600000,
        hash: 'SHA-256',
      },
      deriver,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  },
  wrapAesKey: (aesKey, publicRsaKey) => {
    return window.crypto.subtle.wrapKey('raw', aesKey, publicRsaKey, {
      name: 'RSA-OAEP',
    });
  },
  unWrapAesKey: (
    aesKey,
    privateRsaKey,
    extractable = true,
    keyUsages = ['encrypt']
  ) => {
    return window.crypto.subtle.unwrapKey(
      'raw',
      aesKey,
      privateRsaKey,
      { name: 'RSA-OAEP' },
      { name: 'AES-GCM' },
      extractable,
      keyUsages
    );
  },
  decryptAesGcm: (payload, key) => {
    //payload arrives as binary array (Uint8Array)
    //First 12 bytes are the initialization vector
    //The rest is the encrypted data
    const initializationVector = payload.slice(0, 12);
    const cipher = payload.slice(12);

    // Decrypt the data and convert it from an arrayBuffer to a Uint8array for consitancy with other methods
    return window.crypto.subtle
      .decrypt({ name: 'AES-GCM', iv: initializationVector }, key, cipher)
      .then((buffer) => {
        return new Uint8Array(buffer);
      });
  },
  decryptRsaOaep: (payload, key) => {
    return window.crypto.subtle.decrypt({ name: 'RSA-OAEP' }, key, payload);
  },
  encryptPayloadAndWrapKey: async (payload, publicRsaKeys) => {
    if (typeof window.crypto.subtle === 'undefined') {
      const empty = new Uint8Array(0);
      return Promise.resolve([null, null]);
    }
    const aesKey = await AboboCrypto.generateAesGcmKey();
    const iv = AboboCrypto.generateInitializationVector();

    if (typeof payload == 'string') payload = new TextEncoder().encode(payload);

    const encryptedPayloadBuffer = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      aesKey,
      payload
    );

    const encryptedPayload = new Uint8Array(encryptedPayloadBuffer);
    const combinedpayload = new Uint8Array(
      iv.byteLength + encryptedPayload.byteLength
    );
    combinedpayload.set(iv);
    combinedpayload.set(encryptedPayload, iv.byteLength);

    if (!Array.isArray(publicRsaKeys)) {
      publicRsaKeys = [publicRsaKeys];
    }
    const wrappedAesKeysPromises = publicRsaKeys.map((publicRsaKey) => {
      return AboboCrypto.wrapAesKey(aesKey, publicRsaKey).then(
        (wrappedAesKeyBuffer) => {
          return new Uint8Array(wrappedAesKeyBuffer);
        }
      );
    });

    const wrappedAesKeys = await Promise.all(wrappedAesKeysPromises);
    return [combinedpayload, wrappedAesKeys];
  },
  shareClientAccessKey: async (
    grantorsPrivateKey,
    clientAccessKey,
    passKey,
    granteePublicKey
  ) => {
    const cryptoClientAccessKey = await AboboCrypto.loadClientAccessKey(
      grantorsPrivateKey,
      clientAccessKey,
      passKey,
      true
    );
    const cryptoGranteePublicKey = await AboboCrypto.importPublicKey(
      granteePublicKey
    );

    return AboboCrypto.wrapAesKey(
      cryptoClientAccessKey,
      cryptoGranteePublicKey
    ).then((buffer) => AboboCrypto.uInt8ArrayToBase64(new Uint8Array(buffer)));
  },
  loadClientAccessKey: async (
    operatorPrivateKey,
    clientAccessKey,
    passKey,
    exportable = false
  ) => {
    // First: Ask Operator for their Password
    // Second: Use password to decrypt operators private key using aes
    // Third: Use Operator's private key to decrypt clinic access key
    const decodedOperatorPrivateKey =
      AboboCrypto.base64ToUint8Array(operatorPrivateKey);
    const decodedClientAccessKey =
      AboboCrypto.base64ToUint8Array(clientAccessKey);

    // Passkey arrives as binary digest of the users passphrase
    // const cryptoPassKey = await AboboCrypto.importAesGcmKey(passKey);

    // Decrypt and import the operator's private key
    const decryptedOperatorPrivateKey = await AboboCrypto.importPrivateKey(
      await AboboCrypto.decryptAesGcm(decodedOperatorPrivateKey, passKey),
      'raw'
    );
    const decryptedClientAccessKey = await AboboCrypto.decryptRsaOaep(
      decodedClientAccessKey,
      decryptedOperatorPrivateKey
    );

    // Import the decrypted client access key that lets the operator access the client's private key
    const cryptoClientAccessKey = await AboboCrypto.importAesGcmKey(
      decryptedClientAccessKey,
      exportable
    );

    return cryptoClientAccessKey;
  },
  loadClientPrivateKey: async (clientPrivateKey, cryptoClientAccessKey) => {
    // Fourth: Decrypt the clinic's private key using the clinic access key
    // clientPrivateKey key arrive Base64 encoded from the API
    // need to turn them into binary before decrypting
    const decodedClientPrivateKey =
      AboboCrypto.base64ToUint8Array(clientPrivateKey);

    // Decrypt import and return the client's private key for use elsewhere
    return AboboCrypto.importPrivateKey(
      await AboboCrypto.decryptAesGcm(
        decodedClientPrivateKey,
        cryptoClientAccessKey
      ),
      'raw'
    );
  },
  decryptPayload: async (payload, encryptionKey, clientPrivateKey) => {
    // Fifth: Decrypt the responses encryption key using clinics private key
    // Sixth: Decrypt the response using the encryption key

    const decryptedResponseEncryptionKey = await AboboCrypto.decryptRsaOaep(
      encryptionKey,
      clientPrivateKey
    );

    const cryptoResponseEncryptionKey = await AboboCrypto.importAesGcmKey(
      decryptedResponseEncryptionKey
    );
    const decryptedPayload = await AboboCrypto.decryptAesGcm(
      payload,
      cryptoResponseEncryptionKey
    );

    return decryptedPayload;
  },
};
